import React from "react";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface GroupHeaderProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onToggle"> {
  groupItem: any;
  isPrimary: boolean;
  isSecondary: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

// forwardRef + prop spread so a Radix ContextMenuTrigger `asChild` can clone the
// group-header row as its trigger (see main-RADIX-ASCHILD).
export const GroupHeader = React.memo(
  React.forwardRef<HTMLDivElement, GroupHeaderProps>(function GroupHeader(
    { groupItem, isPrimary, isSecondary, isExpanded, onToggle, className, ...rest },
    ref
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center transition-all duration-200 border-b",
          isPrimary && "bg-muted/50 hover:bg-muted/70 border-border border-b-2",
          isSecondary && "bg-muted/35 hover:bg-muted/50 border-border ml-8",
          className
        )}
        style={{ minWidth: "max-content" }}
        {...rest}
      >
        {/* Toggle Button */}
        <div className="flex-shrink-0 w-12 p-4 flex items-center justify-center">
          <button type="button"
            onClick={onToggle}
            className={cn(
              "p-1.5 rounded transition-all duration-200",
              isPrimary && "hover:bg-muted/80",
              isSecondary && "hover:bg-muted/80"
            )}
            title={isExpanded ? "Collapse group" : "Expand group"}
            aria-expanded={isExpanded}
          >
            <ChevronDown
              className={cn(
                "h-5 w-5 transition-transform duration-200",
                !isExpanded && "-rotate-90"
              )}
            />
          </button>
        </div>

        {/* Group Content */}
        <div className={cn("flex-1 p-4", isSecondary && "pl-2")}>
          <div className="flex items-center gap-3">
            {/* Level Indicator for Secondary Groups */}
            {isSecondary && <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full" />}

            {/* Group Label */}
            <span
              className={cn(
                "font-semibold text-card-foreground truncate",
                isPrimary ? "text-base" : "text-sm text-blue-900 dark:text-blue-100"
              )}
            >
              {groupItem.groupLabel}
            </span>

            {/* Item Count Badge */}
            <Badge
              variant={isPrimary ? "secondary" : "outline"}
              className={cn(
                "flex-shrink-0 text-xs font-medium",
                isSecondary && "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800"
              )}
            >
              {groupItem.itemCount}
            </Badge>

            {/* Secondary Group Label */}
            {isSecondary && (
              <span className="flex-shrink-0 text-xs text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/40 px-2 py-1 rounded font-medium">
                Sub-group
              </span>
            )}
          </div>
        </div>
      </div>
    );
  })
);

GroupHeader.displayName = "GroupHeader";
